// Token Management Utility
// Centralized session handling for web + native platforms

import { API_CONFIG, API_VERSION, ENDPOINTS, SECURITY_HEADERS, STORAGE_KEYS } from "@/lib/api/config";
import { Capacitor } from "@capacitor/core";
import { StashtAppGroup } from "@/plugins/StashtAppGroup";
import { secureTokenStorage } from "@/utils/secure-token-storage";
import * as Sentry from "@sentry/react";

interface TokenInfo {
  accessToken: string;
  refreshToken: string | null;
  expiresAt: number;
  createdAt: number;
}

interface TokenPair {
  accessToken: string;
  refreshToken: string | null;
}

class AuthSessionError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = "AuthSessionError";
    this.status = status;
  }
}

const TOKEN_EXPIRY_BUFFER_MS = 5 * 60 * 1000;

const base64UrlDecode = (value: string): string => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = normalized.length % 4;
  const padded = padding === 0 ? normalized : normalized + "=".repeat(4 - padding);
  return atob(padded);
};

const decodeJwtPayload = (token: string): Record<string, unknown> | null => {
  const tokenParts = token.split(".");
  if (tokenParts.length !== 3) return null;

  try {
    const decoded = base64UrlDecode(tokenParts[1]);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch (error) {
    console.warn("Failed to decode JWT payload:", error);
    return null;
  }
};

const isNative = () => Capacitor.isNativePlatform();
const isIOS = () => isNative() && Capacitor.getPlatform() === "ios";

// Always use Bearer tokens, never cookies (simplified auth model)
const shouldPersistWebTokens = () => !isNative();
const readSessionMarker = () => {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(STORAGE_KEYS.SESSION_ACTIVE) === "true";
  } catch (error) {
    console.warn("Failed to read session marker:", error);
    return false;
  }
};

const writeSessionMarker = (active: boolean) => {
  if (typeof window === "undefined") return;
  try {
    if (active) {
      localStorage.setItem(STORAGE_KEYS.SESSION_ACTIVE, "true");
    } else {
      localStorage.removeItem(STORAGE_KEYS.SESSION_ACTIVE);
    }
  } catch (error) {
    console.warn("Failed to update session marker:", error);
  }
};

export class TokenManager {
  private static instance: TokenManager;
  private tokenCache: TokenInfo | null = null;
  private hydrationPromise: Promise<void> | null = null;
  private refreshPromise: Promise<TokenPair> | null = null;
  private hydrated = false;

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  private buildTokenInfo(accessToken: string, refreshToken: string | null): TokenInfo {
    const payload = decodeJwtPayload(accessToken);
    const exp = typeof payload?.exp === "number" ? payload.exp : 0;

    return {
      accessToken,
      refreshToken,
      expiresAt: exp * 1000,
      createdAt: Date.now(),
    };
  }

  private setTokenCache(accessToken: string, refreshToken: string | null) {
    this.tokenCache = this.buildTokenInfo(accessToken, refreshToken);
  }

  private getLegacyWebTokens(): TokenPair | null {
    try {
      const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!accessToken || !refreshToken) {
        return null;
      }
      return { accessToken, refreshToken };
    } catch (error) {
      console.warn("Failed to read legacy web tokens:", error);
      return null;
    }
  }

  private clearLegacyWebTokens() {
    try {
      localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.TOKEN_VERSION);
    } catch (error) {
      console.warn("Failed to clear legacy web tokens:", error);
    }
  }

  private persistWebTokens(accessToken: string, refreshToken: string | null) {
    if (!shouldPersistWebTokens()) {
      return;
    }

    try {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      if (refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
      }
      localStorage.setItem(STORAGE_KEYS.TOKEN_VERSION, API_VERSION);
    } catch (error) {
      console.warn("Failed to persist web tokens:", error);
    }
  }

  private async migrateLegacyAppGroupTokens() {
    if (!isIOS()) {
      return;
    }

    try {
      const stored = await StashtAppGroup.getTokens();
      if (!stored?.accessToken) {
        return;
      }

      const refreshToken = stored.refreshToken ?? null;
      await secureTokenStorage.saveTokens(stored.accessToken, refreshToken);

      const persisted = await secureTokenStorage.getTokens();
      if (!persisted?.accessToken) {
        console.warn("Keychain migration failed; keeping legacy app group tokens.");
        this.setTokenCache(stored.accessToken, refreshToken);
        return;
      }

      this.setTokenCache(persisted.accessToken, persisted.refreshToken ?? null);

      // LEGACY TO REMOVE: clear app group tokens after successful migration
      await StashtAppGroup.clearTokens();
    } catch (error) {
      console.warn("Failed to migrate app group tokens:", error);
    }
  }

  async hydrate(): Promise<void> {
    if (this.hydrationPromise) {
      return this.hydrationPromise;
    }

    this.hydrationPromise = this.performHydration();

    try {
      await this.hydrationPromise;
    } finally {
      this.hydrationPromise = null;
    }
  }

  private async performHydration(): Promise<void> {
    // Sync API URL to App Groups for iOS share extension
    if (isIOS()) {
      try {
        await StashtAppGroup.setApiUrl({ url: API_CONFIG.BASE_URL });
      } catch (error) {
        console.warn("Failed to sync API URL to app group:", error);
      }
    }

    if (this.tokenCache) {
      this.hydrated = true;
      return;
    }

    if (!isNative()) {
      const webTokens = this.getLegacyWebTokens();
      if (webTokens) {
        this.setTokenCache(webTokens.accessToken, webTokens.refreshToken);
        this.hydrated = true;
        return;
      }

      this.hydrated = true;
      return;
    }

    const stored = await secureTokenStorage.getTokens();
    if (stored?.accessToken) {
      this.setTokenCache(stored.accessToken, stored.refreshToken ?? null);
      this.hydrated = true;
      return;
    }

    await this.migrateLegacyAppGroupTokens();

    this.hydrated = true;
  }

  /**
   * Synchronous check: does a valid (non-expired) refresh token exist?
   * This is the auth gate — if true, the user has a session. No network call.
   * Mirrors Auth0 CredentialsManager.hasValid() pattern.
   *
   * IMPORTANT: This determines "is the user logged in?" — NOT ensureFreshAccessToken().
   * Access token refresh happens lazily via the API client's 401→refresh→retry flow.
   */
  hasValidSession(): boolean {
    const refreshToken = this.tokenCache?.refreshToken;
    if (!refreshToken) return false;

    const payload = decodeJwtPayload(refreshToken);
    if (!payload) return false;

    const exp = typeof payload.exp === "number" ? payload.exp : 0;
    if (exp === 0) return true; // No expiry claim — treat as valid

    return exp * 1000 > Date.now();
  }

  hasSessionMarker(): boolean {
    return readSessionMarker();
  }

  isTokenExpired(): boolean {
    const tokenData = this.getStoredTokenData();
    if (!tokenData) return true;

    const now = Date.now();
    return tokenData.expiresAt - TOKEN_EXPIRY_BUFFER_MS <= now;
  }

  getStoredTokenData(): TokenInfo | null {
    if (this.tokenCache) {
      return this.tokenCache;
    }

    if (!this.hydrated) {
      void this.hydrate();
    }

    return this.tokenCache;
  }

  getTokens(): TokenPair | null {
    const tokenData = this.getStoredTokenData();
    if (!tokenData) return null;

    return {
      accessToken: tokenData.accessToken,
      refreshToken: tokenData.refreshToken,
    };
  }

  getAccessToken(): string | null {
    return this.getTokens()?.accessToken ?? null;
  }

  getRefreshToken(): string | null {
    return this.getTokens()?.refreshToken ?? null;
  }

  async storeTokens(accessToken: string, refreshToken?: string | null) {
    if (!accessToken) {
      console.warn("Attempted to store empty access token");
      return;
    }

    let resolvedRefreshToken = refreshToken ?? this.tokenCache?.refreshToken ?? null;

    if (!resolvedRefreshToken && isNative()) {
      const stored = await secureTokenStorage.getTokens();
      resolvedRefreshToken = stored?.refreshToken ?? null;
    }

    this.setTokenCache(accessToken, resolvedRefreshToken);

    if (isNative()) {
      await secureTokenStorage.saveTokens(accessToken, resolvedRefreshToken);
    } else {
      this.persistWebTokens(accessToken, resolvedRefreshToken);
    }

    writeSessionMarker(true);

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Tokens stored",
      level: "info",
      data: {
        platform: Capacitor.getPlatform(),
        isNative: isNative(),
        hasRefreshToken: !!resolvedRefreshToken,
      },
    });
  }

  async refreshAccessToken(options: { refreshTokenOverride?: string } = {}): Promise<TokenPair> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Token refresh started",
      level: "info",
      data: {
        platform: Capacitor.getPlatform(),
        isNative: isNative(),
        hasOverride: !!options.refreshTokenOverride,
      },
    });

    this.refreshPromise = this.performTokenRefresh(options);

    try {
      return await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  private async performTokenRefresh(options: { refreshTokenOverride?: string }): Promise<TokenPair> {
    const refreshToken = options.refreshTokenOverride ?? this.getRefreshToken();

    if (!refreshToken) {
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Token refresh failed - no refresh token",
        level: "warning",
      });
      throw new AuthSessionError("No refresh token available", 401);
    }

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => abortController.abort(), API_CONFIG.TIMEOUT);

    const headers: HeadersInit = {
      "content-type": "application/json",
    };

    const apiKey = SECURITY_HEADERS["x-api-key"];
    if (apiKey) {
      headers["x-api-key"] = apiKey;
    }

    let response: Response;
    try {
      response = await fetch(`${API_CONFIG.BASE_URL}${ENDPOINTS.AUTH.REFRESH}`, {
        method: "POST",
        headers,
        body: JSON.stringify({ refresh_token: refreshToken }),
        credentials: "omit",
        signal: abortController.signal,
      });
    } catch (error) {
      const isTimeout = error instanceof Error && error.name === "AbortError";
      Sentry.addBreadcrumb({
        category: "auth",
        message: isTimeout ? "Token refresh timeout" : "Token refresh network error",
        level: "error",
        data: {
          errorName: error instanceof Error ? error.name : "unknown",
          errorMessage: error instanceof Error ? error.message : String(error),
        },
      });
      if (isTimeout) {
        throw new AuthSessionError("Token refresh timeout", 408);
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      console.error(`Token refresh failed: ${response.status} - ${errorText}`);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Token refresh HTTP error",
        level: "error",
        data: {
          status: response.status,
          errorText: errorText.substring(0, 200),
        },
      });
      throw new AuthSessionError("Token refresh failed", response.status);
    }

    const data = await response.json();
    const newAccessToken = data.access_token || data.accessToken;
    const newRefreshToken = data.refresh_token || data.refreshToken || refreshToken || null;

    if (!newAccessToken) {
      Sentry.addBreadcrumb({
        category: "auth",
        message: "Token refresh response missing access token",
        level: "error",
        data: { status: response.status },
      });
      throw new AuthSessionError("Token refresh response missing access token", response.status);
    }

    await this.storeTokens(newAccessToken, newRefreshToken);

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Token refresh successful",
      level: "info",
      data: {
        status: response.status,
        hasNewRefreshToken: newRefreshToken !== refreshToken,
      },
    });

    return {
      accessToken: newAccessToken,
      refreshToken: newRefreshToken,
    };
  }

  async ensureFreshAccessToken(): Promise<string | null> {
    await this.ensureHydrated();

    const tokens = this.getTokens();

    if (!tokens?.accessToken) {
      // No access token and no session marker means user isn't logged in
      if (!readSessionMarker()) {
        return null;
      }
      try {
        const refreshed = await this.refreshAccessToken();
        return refreshed.accessToken;
      } catch {
        return null;
      }
    }

    if (!this.isTokenExpired()) {
      return tokens.accessToken;
    }

    try {
      const refreshed = await this.refreshAccessToken();
      return refreshed.accessToken;
    } catch {
      return null;
    }
  }

  getCredentialsMode(): RequestCredentials {
    return "omit";
  }

  async clearAllTokens() {
    const hadAccessToken = !!this.tokenCache?.accessToken;
    const hadRefreshToken = !!this.tokenCache?.refreshToken;

    Sentry.addBreadcrumb({
      category: "auth",
      message: "Clearing all tokens",
      level: "info",
      data: {
        platform: Capacitor.getPlatform(),
        isNative: isNative(),
        hadAccessToken,
        hadRefreshToken,
      },
    });

    this.tokenCache = null;

    if (isNative()) {
      await secureTokenStorage.clearTokens();
    } else {
      this.clearLegacyWebTokens();
    }

    writeSessionMarker(false);

    if (isIOS()) {
      // LEGACY TO REMOVE: clear app group tokens after migration window
      try {
        await StashtAppGroup.clearTokens();
      } catch (error) {
        console.warn("Failed to clear app group tokens:", error);
      }
    }
  }

  forceClearAllAuth() {
    void this.clearAllTokens();

    try {
      localStorage.removeItem(STORAGE_KEYS.API_VERSION);

      Object.keys(localStorage).forEach(key => {
        if (key.startsWith("stasht_")) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.warn("Failed to fully clear local storage:", error);
    }
  }

  showAuthStatus() {
    const tokenData = this.getStoredTokenData();
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);

    console.group("Auth Status Report");
    console.log("API Version:", API_VERSION);

    if (tokenData) {
      const isExpired = this.isTokenExpired();
      const expiresIn = Math.floor((tokenData.expiresAt - Date.now()) / 1000 / 60);
      console.log("Access Token:", tokenData.accessToken.substring(0, 20) + "...");
      console.log("Token Expired:", isExpired ? "yes" : "no");
      console.log("Expires in:", isExpired ? "expired" : `${expiresIn} minutes`);
    } else {
      console.log("No tokens stored");
    }

    if (userData) {
      try {
        const user = JSON.parse(userData);
        console.log("User:", user.email || user.user_id);
      } catch {
        console.log("User data corrupted");
      }
    }

    console.groupEnd();
  }

  // Deprecated: we no longer force re-auth based on version mismatches.
  shouldForceReauth(): boolean {
    return false;
  }

  getTimeUntilExpiry(): number {
    const tokenData = this.getStoredTokenData();
    if (!tokenData) return 0;

    const timeLeft = tokenData.expiresAt - Date.now();
    return Math.max(0, Math.floor(timeLeft / 1000 / 60));
  }

  isHydrated(): boolean {
    return this.hydrated;
  }

  async ensureHydrated(): Promise<void> {
    if (this.hydrated) return;
    await this.hydrate();
  }
}

// Global instance
export const tokenManager = TokenManager.getInstance();

// Export convenience functions
export const clearAuthData = () => tokenManager.forceClearAllAuth();
export const showAuthStatus = () => tokenManager.showAuthStatus();
export const getTokens = () => tokenManager.getTokens();
export const getAccessToken = () => tokenManager.getAccessToken();
